import TextField from '@components/v2/form/TextField/TextField';
import { Stack } from '@mui/material';
import Box from '@mui/material/Box';
import lodash from 'lodash';
import { memo, useRef, useState } from 'react';
import LangEnum from '../../../lang/LangEnum';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import useWidth from '../../Hooks/useWidth';
import MultiOptionsSelectorFooter, {
  MultiOptionsSelectorFooterParams,
} from './components/MultiOptionsSelectorFooter';
import MultiOptionsSelectorHeader, {
  MultiOptionsSelectorHeaderParams,
} from './components/MultiOptionsSelectorHeader';
import OptionsList from './components/OptionsList/OptionsList';
import SearchFieldWrapper from './components/SearchFieldWrapper/SearchFieldWrapper';
import useDynamicOptionsSelector from './hooks/useDynamicOptionsSelector';
import { UseDynamicOptionsSelectorProps } from './hooks/useMultiOptionsSelector.types';

export interface DynamicOptionsSelectorProps extends UseDynamicOptionsSelectorProps {
  hideSearchField?: boolean;
  canReorder?: boolean;
  label?: string;
  optionsPaddingInline?: number;
  fullWidth?: boolean;
  fillHeight?: boolean;
  titleProps?: Pick<MultiOptionsSelectorHeaderParams, 'title'>;
  footerProps?: Pick<MultiOptionsSelectorFooterParams, 'actions'>;
}

const DynamicOptionsSelectorV2 = ({
  options,
  hideSearchField,
  onChange,
  value,
  isLoadingOptions,
  loadMoreOptions,
  canReorder,
  label,
  optionsPaddingInline = 0,
  fullWidth,
  fillHeight,
  titleProps,
  footerProps,
}: DynamicOptionsSelectorProps) => {
  const { translate } = useTranslation();
  const { mobileWidth } = useWidth();
  const desktopWidth = fullWidth ? '100%' : 325;
  const desktopMaxHeight = mobileWidth ? '100%' : '500px';
  const [searchTerm, setSearchTerm] = useState('');

  const { textFieldRef, query, setQuery, handleScroll, handleSelectMultiple } =
    useDynamicOptionsSelector({
      options,
      value,
      loadMoreOptions,
      onChange,
      isLoadingOptions,
    });

  const onChangeInput = (value: string) => {
    setQuery(value);
  };

  const debouncedInputChange = useRef(
    lodash.debounce((value: string) => {
      onChangeInput(value);
    }, 500),
  ).current;

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    debouncedInputChange(value);
  };

  return (
    <Stack
      direction="column"
      sx={{ flex: fillHeight ? 1 : undefined, minHeight: fillHeight ? 0 : undefined }}
    >
      {titleProps && <MultiOptionsSelectorHeader {...titleProps} />}
      <Box
        sx={{
          maxHeight: fillHeight ? 'none' : desktopMaxHeight,
          gap: '5px',
          display: 'flex',
          overflow: fillHeight ? 'hidden' : 'auto',
          flexGrow: fillHeight || mobileWidth ? 1 : 0,
          minHeight: fillHeight ? 0 : undefined,
          flexDirection: 'column',
          width: mobileWidth ? 'auto' : desktopWidth,
        }}
      >
        {!hideSearchField && (
          <SearchFieldWrapper
            paddingInline={optionsPaddingInline}
            sx={fillHeight ? { paddingRight: 1.5 } : undefined}
          >
            <TextField
              size="small"
              label={label ? label : translate(LangEnum.GLOBAL_SEARCH)}
              value={searchTerm}
              ref={textFieldRef}
              onChange={handleChangeInput}
            />
          </SearchFieldWrapper>
        )}
        <Box
          id="dynamic-options-list"
          onScroll={handleScroll}
          sx={{
            overflowY: 'auto',
            overscrollBehavior: 'none',
            flex: fillHeight ? '1 1 auto' : undefined,
            minHeight: fillHeight ? 0 : undefined,
            paddingInline: optionsPaddingInline,
          }}
        >
          <OptionsList
            fillHeight={fillHeight}
            options={options}
            selectedValues={value}
            onSelect={handleSelectMultiple}
            isLoadingOptions={isLoadingOptions}
            query={query}
            canReorder={canReorder}
          />
        </Box>
      </Box>
      {footerProps && <MultiOptionsSelectorFooter {...footerProps} />}
    </Stack>
  );
};

export default memo(DynamicOptionsSelectorV2) as typeof DynamicOptionsSelectorV2;
